import * as angular from "angular"
import { INVZMessage } from "../../../nvz/types"
import { FieldViewModel, ICropping, INtrReqNutrients, isDate, isNumber } from "../../../omnia"
import { getProductUnitsTextNormalizedFromApplication } from "../../../services/units"
import { ICroppingBiomass, INtrReq, INutritionAppRate, INutritionPlan, INutritionPlanCropping, INutritionService, NutrientType, NvzPlanCropping, ProductAppInput } from "../../../types"
import { PlanEditContext } from "../editContext"
import { NutrientVm } from "./nutrientVm"
import { IAppSummary } from "./types"
import { selectMany } from "@omnia/shared/utils/array"
import { getCropCssColor } from "@omnia/shared/fields"
import { formatChemicalCompoundNameAsHtml } from "@omnia/shared/utils/format"

export interface IRequirementsRatios {
    N: NutrientVm;
    SO3: NutrientVm;
    P2O5: NutrientVm;
    K2O: NutrientVm;
    MgO: NutrientVm;
    Lime: NutrientVm;
}

export class CroppingEditVm implements NvzPlanCropping {

    static createModels(svc: INutritionService, ctxt: PlanEditContext, targetPlan: INutritionPlan): CroppingEditVm[] {

        var requirements = svc.getRequirements();

        var currentCropping = targetPlan.Cropping || [];

        var holdingCropping = selectMany(svc.fields.map(f => f.croppings.filter(c => c.currentYear)));

        return holdingCropping.map(c => {

            var requirement = requirements.find(r => r.CroppingId === c.croppingId && !r.Zone);

            if (!requirement) {
                return null;
            }

            var currentPlanBinding = currentCropping.find(pc => pc.CroppingId == c.croppingId);

            var vm = new CroppingEditVm(svc, ctxt, c, requirement, currentPlanBinding)

            return vm;

        }).filter(c => !!c);

    }

    constructor(
        private svc: INutritionService,
        private ctxt: PlanEditContext,
        public croppingVm: FieldViewModel,
        public requirements: INtrReqNutrients,
        public currentPlanBinding: INutritionPlanCropping,
        public readonly cropping = croppingVm.cropping,
        public readonly croppingId = croppingVm.croppingId,
        public readonly crop = croppingVm.crop,
        public readonly cropId = croppingVm.cropId,
        public readonly authorized = croppingVm.authorized
    ) {

        this.description = croppingVm.getCroppingDescription(true, true);
        this.expanded = true;
        this.selected = !!currentPlanBinding;

        this.biomassAppRate = (!currentPlanBinding) ? {} : {
            Nutrient: currentPlanBinding.BiomassNutrientRate,
            Product: currentPlanBinding.BiomassProductRate
        };

        this.updateCalculatedValues();

    }

    public planRequirements: {
        crop: INtrReq,
        zones: INtrReq[]
    };

    selected: boolean;
    expanded: boolean;

    remove: boolean;

    description: string;

    applications: IAppSummary[];
    requirementRatios: IRequirementsRatios;
    nvzMessages: INVZMessage[];

    biomassLayers: ICroppingBiomass[]
    biomassLayer: ICroppingBiomass
    biomassAppRate: INutritionAppRate

    setupBiomass(layers: ICroppingBiomass[]) {

        this.biomassLayers = layers

        const pb = this.currentPlanBinding
        this.biomassLayer = pb ? this.biomassLayers.find(l => l.LayerId === pb.BiomassLayerId) : null

    }

    updateTargetModel(pc: INutritionPlanCropping) {

        var c = this;

        if (c.currentPlanBinding) {
            pc.NutritionCroppingId = c.currentPlanBinding.NutritionCroppingId;
        }

        pc.CroppingId = c.croppingId;

        this.updateModelBiomass(pc);

    }

    private updateModelBiomass(pc: INutritionPlanCropping) {

        let useBiomass = this.ctxt && this.ctxt.useBiomass;

        pc.BiomassLayerId = useBiomass && this.biomassLayer ? this.biomassLayer.LayerId : null;
        pc.BiomassNutrientRate = null;
        pc.BiomassProductRate = null;

        if (useBiomass && this.biomassAppRate && (!this.biomassLayer)) {
            pc.BiomassNutrientRate = this.biomassAppRate.Nutrient;
            pc.BiomassProductRate = this.biomassAppRate.Product;
        }

    }


    get planId(): number {
        if (this.ctxt) {
            return this.ctxt.target.NutritionPlanId;
        }
    }

    getCropColor() {
        return getCropCssColor(this.croppingVm.cropping.Crop);
    }

    getDescription(includeCrop: boolean = true): string {
        return this.croppingVm.getCroppingDescription(true, false, includeCrop);
    }

    updateCalculatedValues() {

        this.initOpeningRequirements();
        this.updateCroppingApplications();
        this.requirementRatios = this.getRequirementsRatios();

    }

    private initOpeningRequirements() {

        let openingReq = this.svc.getRequirementsProvider().getPlanRequirements(this.croppingId, this.planId);

        let crop = openingReq.find(r => !r.Zone) || <any>{};

        let zones = openingReq.filter(r => !r.Aggregate);

        // 171121 - Usersnap 742
        // We are now keeping "single, whole-field zones", rather than replacing them with the aggregate
        // if (zones.length <= 1) zones = [crop];
        if (zones.length <= 0) zones = [crop];

        this.planRequirements = {
            crop,
            zones
        }

    }

    public updateCroppingApplications() {

        const calcSvc = this.svc.calculationService

        const current = this.applications || []

        const apps = (this.ctxt.applications || [])
            .filter(a => a.isValid())
            .map(appVm => {

                const app = appVm.productApp

                const req = this.planRequirements.crop

                const rounding = appVm.showTonnes() ? 10 : 1

                const { amount, area } = calcSvc.calculateProductAmount(this.planRequirements, app, this.cropping)

                const ovr = appVm.getCroppingOverride(this.croppingId)

                // 210308 - Usersnap 2053
                // The product rate should now be calculated based on the total amount + area
                // The previous calculation method, based on crop-level averages, would fail on cases where
                // the input requirement zones had large areas with no requirements, leading to an average of 0
                const productRate = (ovr && (ovr.LastInputUsed == ProductAppInput.ProductRate))
                    ? ovr.ProductRate
                    : amount && area
                        ? Math.round(amount / area * rounding) / rounding
                        : calcSvc.calculateProductRate(req, app, this.cropping)

                // 210308 - Usersnap 2053
                // For consistency, the nutrient rate is now also calculated based on the product rate calcualted before
                const nutrientRate = (ovr && (ovr.LastInputUsed == ProductAppInput.NutrientRate))
                    ? ovr.NutrientRate
                    : Math.round(calcSvc.calculateNutrientRateFromProductRate(productRate, app, app.TargetNutrient, this.cropping) * rounding) / rounding

                const nutrientPct = !ovr
                    ? appVm.nutrientPct
                    : (ovr && (ovr.LastInputUsed == ProductAppInput.NutrientPct))
                        ? ovr.NutrientPct
                        : undefined

                const unit = getProductUnitsTextNormalizedFromApplication(app)
                const nutrientUnit = getProductUnitsTextNormalizedFromApplication(app, true)

                const target: Partial<IAppSummary> = current.find(c => c.vm == appVm) || { vm: appVm }

                angular.extend(target, {
                    name: app.Fertiliser ? app.Fertiliser.Name : app.Manure ? app.Manure.Name : "",
                    productRate,
                    nutrientRate,
                    nutrientPct,
                    total: amount,
                    unit,
                    nutrientUnit,
                    targetNutrientHtml: formatChemicalCompoundNameAsHtml(NutrientType[app.TargetNutrient]),
                    date: app.ApplicationDate,
                    decimals: app.Fertiliser ? 0 : 1,
                    application: app,
                    vm: appVm
                })

                return target

            });

        this.applications = apps.map(a => <IAppSummary>a)

    }

    public getRequirementsLeft(): INtrReqNutrients {

        const plan = this.ctxt.target;

        const applications = (plan.Applications || []).filter(a =>
            isDate(a.ApplicationDate)
            && (isNumber(a.NutrientRate) || isNumber(a.NutrientPct) || isNumber(a.ProductRate))
        )

        let calcSvc = this.ctxt.svc.calculationService;

        return calcSvc.calculateClosingRequirements(this.planRequirements.crop, applications, this.cropping, false);

    }

    public getRequirementsRatios(): IRequirementsRatios {

        var initial = this.requirements;
        var closing = this.getRequirementsLeft();
        var opening = this.planRequirements.crop;

        let ratios = {
            N: NutrientVm.create(initial, closing, opening, "N"),
            SO3: NutrientVm.create(initial, closing, opening, "SO3"),
            P2O5: NutrientVm.create(initial, closing, opening, "P2O5"),
            K2O: NutrientVm.create(initial, closing, opening, "K2O"),
            MgO: NutrientVm.create(initial, closing, opening, "MgO"),
            Lime: NutrientVm.create(initial, closing, opening, "Lime", 10)
        }

        return ratios;

    }

    public getNVZMessagesHtml(app: IAppSummary): string {

        let messages = this.getNVZMessages(app.vm.editId)

        if (messages && messages.length > 0) {

            let errors = this.getNVZMessagesHtmlType(messages.filter(m => !m.info), false)
            let infos = this.getNVZMessagesHtmlType(messages.filter(m => !!m.info), true)

            return errors + infos;

        }

        return "";
    }

    private getNVZMessagesHtmlType(messages: INVZMessage[], info: boolean): string {

        if (messages && messages.length > 0) {

            let text = messages.map(m => m.description).join("<br/>");
            let cssClass = info ? "nvz-info" : "nvz-errors";

            text = `<p class='${cssClass}'>${text}</p>`;

            return text;


        }

        return "";

    }

    public getNVZMessages(editId: number): INVZMessage[] {
        return (this.nvzMessages || []).filter(m => m.application._EditId === editId)
    }

    public hasNVZWarnings(): boolean {
        if (this.nvzMessages) {
            return this.nvzMessages.some(m => !m.info);
        }
    }

    public hasNVZMessages(): boolean {
        return this.nvzMessages && (this.nvzMessages.length > 0);
    }

}
